import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { KioskCustomLanguage, SwipeCardConfigs, VirtualSignInOptions } from 'utils/types';
import VirtualCheckInContainer from './VirtualCheckInContainer';
import PhysicalCheckInContainer from './PhysicalCheckInContainer';
import {
    hasMoreThanOne,
    hasNotCheckedInOrIsSingleServiceKiosk,
    hasNotVisitedServiceWithAppointment,
    isCheckedInAnotherKioskAndNotInThisSingleServiceKiosk,
    isCheckedInBesidesSelectedService,
    isCheckedInIntoOneServiceInAMultiServiceKiosk,
    isCheckedInOneService,
    isNotCheckedInAMultiServiceKiosk,
    isSingleServiceKiosk,
    isUserCheckedInSelectedService,
    servicesCheckedIn,
    isCheckedInAnotherServiceInAMultiServiceKioskAndNotInSelectedService,
    isCheckedInAnotherServiceInAMultiServiceKioskAndInSelectedService
} from 'utils/kioskRouteHandler';
import { isCheckedIn, isScheduledOnly, isSelfService, STEPS } from 'utils/kiosk';
import AuthenticatedCheckInContainer from './AuthenticatedCheckInContainer';

/**
 * @param {Object} props - see propTypes
 * @returns {Object} Loads the Kiosk and controls it's state.
 */
function App(props) {
    const [error, setError] = useState(null);
    const [warning, setWarning] = useState(null);
    const [studentName, setStudentName] = useState("");
    const [studentEmail, setStudentEmail] = useState("");
    const [studentId, setStudentId] = useState(null);
    const [isProspectiveStudent, setIsProspectiveStudent] = useState(false);
    const [service, setService] = useState({});
    const [services, setServices] = useState([]);
    const [otherKioskServices, setOtherKioskServices] = useState([]);
    const [reason, setReason] = useState({});
    const [course, setCourse] = useState({});
    const [appointmentStartTime, setAppointmentStartTime] = useState("");
    const [appointmentInstructor, setAppointmentInstructor] = useState({});
    const [groupSession, setGroupSession] = useState({});
    const [appointmentId, setAppointmentId] = useState(null);
    const [waitingRoomQueueId, setWaitingRoomQueueId] = useState(null);
    const [scheduleBlockId, setScheduleBlockId] = useState(null);
    const [waitTime, setWaitTime] = useState(0);
    const [servicesIdWithAppointmentVisited, setServicesIdWithAppointmentVisited] = useState([]);
    const [hasVisitedMultipleCheckInOptions, setVisitedHasMultipleCheckInOptions] = useState(false);
    
    const authenticatedVirtualSignInForSpecificService = () =>
        props.virtualSignInOptions.virtualSignInEnabled
        && props.virtualSignInOptions.virtualServiceId 
        && props.virtualSignInOptions.virtualStudentId;

    const clearNotifications = () => {
        setWarning("");
        setError("")
    };

    const setServiceWithAppointmentAsVisited = (visitedService) => {
        servicesIdWithAppointmentVisited.push(visitedService.serviceId);
        setServicesIdWithAppointmentVisited(servicesIdWithAppointmentVisited);
    };

    const deleteLastVisitedServiceWithAppointment = (step) => {
        //Don't want to remove visited appointment service each time the back button is pressed
        if (step === STEPS.SELECT_SERVICE && servicesIdWithAppointmentVisited.includes(service.serviceId)) {
            servicesIdWithAppointmentVisited.pop();
            setServicesIdWithAppointmentVisited(servicesIdWithAppointmentVisited)
        }
    };

    const shouldSkipCourseAndMoreInformationPages = () => {
        return !props.showCoursePage && !props.showMoreInformationPage;
    };
    
    const setLoginParameters = (loginParameters) => {
        if (loginParameters.serviceId) {
            setService({
                name: loginParameters.name,
                courseDisplayName: loginParameters.courseDisplayName,
                serviceId: loginParameters.serviceId,
                serviceUid: loginParameters.serviceUid,
                appointmentId: loginParameters.appointmentId
            });
        } else {
            setService({});
        }

        setAppointmentId(loginParameters.appointmentId);
        setWaitingRoomQueueId(loginParameters.waitingRoomQueueId);
        setScheduleBlockId(loginParameters.scheduleBlockId);
        setAppointmentStartTime(loginParameters.appointmentStartTime);
        setWaitTime(loginParameters.waitTime);
    };
    
    const clearSelections = () => {
        setReason({});
        setGroupSession({});
        setCourse({});
        setServicesIdWithAppointmentVisited([]);
        setVisitedHasMultipleCheckInOptions(false);
    };

    const displayServiceName = specificService =>
        specificService.courseDisplayName ? specificService.courseDisplayName : specificService.name;

    const processSignIn = (json, isProspective = false) => {
        clearSelections();
        clearNotifications();
        setStudentName(json.studentName);
        setIsProspectiveStudent(isProspective);
        setStudentId(json.partyId || null);
        setStudentEmail(json.studentEmail || null);

        const kioskServices = json.services || [];
        setServices(kioskServices);

        const servicesFromOtherKiosks = json.servicesInOtherKiosks || [];
        setOtherKioskServices(servicesFromOtherKiosks);

        const waitingRoomStatus = json.waitingRoomStatus || {};

        const serviceWithAppointment =
            kioskServices.find(kioskService => json.appointment && kioskService.appointmentId === json.appointment);

        if (json.appointment) {
            setAppointmentInstructor({
                name: json.instructorName,
                uid: json.instructorUid,
                title: json.instructorTitle
            });
        }

        if (authenticatedVirtualSignInForSpecificService() &&
          (isCheckedInAnotherServiceInAMultiServiceKioskAndNotInSelectedService(kioskServices, waitingRoomStatus)
            || isCheckedInAnotherServiceInAMultiServiceKioskAndInSelectedService(kioskServices, waitingRoomStatus))) {
            setLoginParameters(waitingRoomStatus);
        } else if (isCheckedInIntoOneServiceInAMultiServiceKiosk(kioskServices)) {
            setLoginParameters(kioskServices.filter(isCheckedIn)[0]);
        } else if (serviceWithAppointment && isNotCheckedInAMultiServiceKiosk(kioskServices)) {
            setLoginParameters(serviceWithAppointment);
        } else if (waitingRoomStatus && hasNotCheckedInOrIsSingleServiceKiosk(kioskServices)) {
            // Need to fallback to the first service because sometimes we don't have the information from the waiting room
            // and need to display that information on the header
            const firstKioskService = kioskServices[0] || {};

            const getCourseDisplayName = () => {
                if ( isSingleServiceKiosk(kioskServices) ) return displayServiceName(firstKioskService);
                if ( waitingRoomStatus.name ) return waitingRoomStatus.name;
                return firstKioskService.name;
            };

            setLoginParameters({
                name: waitingRoomStatus.name || firstKioskService.name,
                courseDisplayName: getCourseDisplayName(),
                serviceUid: waitingRoomStatus.serviceUid || firstKioskService.serviceUid,
                serviceId: waitingRoomStatus.serviceId || firstKioskService.serviceId,
                appointmentId: waitingRoomStatus.appointmentId || firstKioskService.appointmentId,
                appointmentStartTime: waitingRoomStatus.appointmentStartTime || firstKioskService.appointmentStartTime,
                waitingRoomQueueId: waitingRoomStatus.waitingRoomQueueId || firstKioskService.waitingRoomQueueId,
                waitTime: waitingRoomStatus.waitTime || 0,
                scheduleBlockId: waitingRoomStatus.scheduleBlockId || firstKioskService.scheduleBlockId
            });
        } else {
            setLoginParameters({});
        }

        // student is checked into another service in this multi-service kiosk, so take them to screen
        // checking if they want to proceed with checking in
        if (authenticatedVirtualSignInForSpecificService()
          && isCheckedInAnotherServiceInAMultiServiceKioskAndNotInSelectedService(kioskServices, waitingRoomStatus)) {
            return STEPS.MULTIPLE_CHECKINS_OPTION
        // student is already checked into this service and into another service in this multi-service kiosk, so take
        // them to checked in options
        } else if (authenticatedVirtualSignInForSpecificService()
          && isCheckedInAnotherServiceInAMultiServiceKioskAndInSelectedService(kioskServices, waitingRoomStatus)) {
            return STEPS.CHECKED_IN_OPTIONS;
        // student is already checked into this service, so take them to checked in options
        } else if (isCheckedInAnotherKioskAndNotInThisSingleServiceKiosk(servicesFromOtherKiosks, kioskServices)) {
            return STEPS.MULTIPLE_CHECKINS_OPTION
        } else if (hasMoreThanOne(servicesCheckedIn(kioskServices))) {
            return STEPS.SELECT_SERVICE;
        } else if (isCheckedInOneService(waitingRoomStatus, kioskServices)) {
            return STEPS.CHECKED_IN_OPTIONS;
        } else if (json.appointment && !isSelfService(props.kioskType)) {
            return STEPS.CONFIRM_APPOINTMENT;
        } else if (authenticatedVirtualSignInForSpecificService() && isScheduledOnly(props.kioskType)) {
            return STEPS.NO_APPOINTMENT_SCHEDULED;
        } else if (authenticatedVirtualSignInForSpecificService() && waitingRoomStatus.serviceId > 0) {
            return STEPS.SELECT_REASON;
        } else {
            return STEPS.SELECT_SERVICE;
        }
    };

    const selectService = (selectedService, replace = false) => {
        clearNotifications();
        setService(selectedService);
        setScheduleBlockId(scheduleBlockId || selectedService.scheduleBlockId);
        setWaitingRoomQueueId(waitingRoomQueueId || selectedService.waitingRoomQueueId);
        const studentAppointmentId = selectedService.appointmentId;
        setAppointmentId(studentAppointmentId);
        setAppointmentStartTime(selectedService.appointmentStartTime);
        setWaitTime(selectedService.waitTime);
        const allServices = services.concat(otherKioskServices);

        if (replace) {
            return isScheduledOnly(props.kioskType) ? STEPS.NO_APPOINTMENT_SCHEDULED : STEPS.SELECT_REASON;
        } else if (isScheduledOnly(props.kioskType) && !studentAppointmentId) {
            return STEPS.NO_APPOINTMENT_SCHEDULED;
        } else if (isCheckedInBesidesSelectedService(allServices, selectedService) && hasNotVisitedServiceWithAppointment(selectedService, servicesIdWithAppointmentVisited) && !hasVisitedMultipleCheckInOptions) {
            return STEPS.MULTIPLE_CHECKINS_OPTION;
        } else if (isUserCheckedInSelectedService(allServices, selectedService)) {
            return STEPS.CHECKED_IN_OPTIONS;
        } else if (studentAppointmentId && hasNotVisitedServiceWithAppointment(selectedService, servicesIdWithAppointmentVisited) && !isSelfService(props.kioskType)) {
            return STEPS.CONFIRM_APPOINTMENT;
        } else {
            return STEPS.SELECT_REASON;
        }
    };

    const selectReason = (selectedReason) => {
        setReason(selectedReason);
        setGroupSession({});
        if(isProspectiveStudent) {
            return STEPS.MORE_INFORMATION;
        } else if (selectedReason.groupSessionAvailable) {
            return STEPS.SELECT_MEETING_TYPE_PAGE;
        } else if (props.showCoursePage) {
            return STEPS.SELECT_COURSE;
        } else {
            return STEPS.MORE_INFORMATION;
        }
    };

    const selectGroupSession = (selectedGroupSession) => {
        const shouldSelectCourseForGroupSession = selectedGroupSession.supplementalInstruction
            && !selectedGroupSession.courseRestrictions.length;

        setGroupSession(selectedGroupSession);
        if (props.showCoursePage || shouldSelectCourseForGroupSession) {
            return STEPS.SELECT_COURSE;
        } else {
            return STEPS.MORE_INFORMATION;
        }
    };

    const selectCourse = (selectedCourse) => {
        setCourse(selectedCourse);
        return STEPS.MORE_INFORMATION;
    };

    const selectOneOnOne = () => props.showCoursePage ? STEPS.SELECT_COURSE : STEPS.MORE_INFORMATION;

    const onConfirmMultipleCheckIn = () => {
        clearNotifications();
        setVisitedHasMultipleCheckInOptions(true);
        if (appointmentId && !isSelfService(props.kioskType)) {
            return STEPS.CONFIRM_APPOINTMENT;
        } else if (isScheduledOnly(props.kioskType)) {
            return STEPS.NO_APPOINTMENT_SCHEDULED;
        } else {
            return STEPS.SELECT_REASON;
        }
    };

    const goToCheckoutConfirmation = (dequeueWarning) => {
        if (dequeueWarning) {
            // In the case of a warning about dequeueing, we will still advance the screen so do not want to clear it
            setError("");
            setWarning(dequeueWarning);
        }
        else clearNotifications();
        return STEPS.CHECKOUT_CONFIRMATION_PAGE;
    };

    const kioskProps = {
        ...props,
        processSignIn: processSignIn,
        deleteLastVisitedServiceWithAppointment: deleteLastVisitedServiceWithAppointment,
        displayServiceName: displayServiceName,
        clearNotifications: clearNotifications,
        setServiceWithAppointmentAsVisited: setServiceWithAppointmentAsVisited,
        shouldSkipCourseAndMoreInformationPages: shouldSkipCourseAndMoreInformationPages,
        selectService: selectService,
        selectReason: selectReason,
        selectGroupSession: selectGroupSession,
        selectCourse: selectCourse,
        selectOneOnOne: selectOneOnOne,
        onConfirmMultipleCheckIn: onConfirmMultipleCheckIn,
        goToCheckoutConfirmation: goToCheckoutConfirmation,
        error: error,
        setError: setError,
        warning: warning,
        setWarning: setWarning,
        studentName: studentName,
        studentEmail: studentEmail,
        studentId: studentId,
        isProspectiveStudent: isProspectiveStudent,
        service: service,
        services: services,
        otherKioskServices: otherKioskServices,
        appointmentInstructor: appointmentInstructor,
        reason: reason,
        groupSession: groupSession,
        appointmentId: appointmentId,
        waitingRoomQueueId: waitingRoomQueueId,
        setWaitingRoomQueueId: setWaitingRoomQueueId,
        scheduleBlockId: scheduleBlockId,
        waitTime: waitTime,
        course: course,
        appointmentStartTime: appointmentStartTime
    };

    if (props.virtualSignInOptions.virtualSignInEnabled && props.virtualSignInOptions.virtualStudentId)
        return <VirtualCheckInContainer {...kioskProps} />;
    else if (props.virtualSignInOptions.virtualSignInEnabled)
        return <AuthenticatedCheckInContainer {...kioskProps} 
                                              showToProspectiveStudents={!!(props.showToProspectiveStudents
                                                && props.virtualSignInOptions.prospectiveStudentsEnabled)} />;
    return <PhysicalCheckInContainer {...kioskProps} />;
}

App.defaultProps = {
    virtualSignInOptions: {
        virtualSignInEnabled: false
    }
};

App.propTypes = {
    customLanguage: KioskCustomLanguage,
    kioskId: PropTypes.number.isRequired,
    kioskImageUrl: PropTypes.string.isRequired,
    kioskName: PropTypes.string.isRequired,
    kioskType: PropTypes.string.isRequired,
    kioskUid: PropTypes.string.isRequired,
    lastNameField: PropTypes.string.isRequired,
    lastNameSignOnDisabled: PropTypes.bool.isRequired,
    loadActiveXListener: PropTypes.func,
    prospectsNeedBirthday: PropTypes.bool,
    prospectsNeedPhone: PropTypes.bool,
    prospectsNeedZip: PropTypes.bool,
    showCoursePage: PropTypes.bool.isRequired,
    showMoreInformationPage: PropTypes.bool.isRequired,
    showToProspectiveStudents: PropTypes.bool.isRequired,
    studentIdField: PropTypes.string.isRequired,
    swipeCardConfigs: SwipeCardConfigs.isRequired,
    unloadActiveXListener: PropTypes.func,
    updateActiveXLastName: PropTypes.func,
    virtualSignInOptions: VirtualSignInOptions.isRequired
};

export default hot(App);
